import { DownloadOutlined } from '@ant-design/icons';
import { Alert, Button, Form, Input, message, Modal, Popover, Select, Tooltip } from 'antd';
import { easeInOut, motion } from 'framer-motion';
import * as sdk from 'microsoft-cognitiveservices-speech-sdk';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { AiFillDislike, AiFillLike, AiOutlineDislike, AiOutlineLike } from 'react-icons/ai';
import { FaEye } from 'react-icons/fa';
import { GiSplitCross } from 'react-icons/gi';
import { IoMdSend } from 'react-icons/io';
import { MdContentCopy } from 'react-icons/md';
import { decodeToken } from 'react-jwt';
import { useDispatch, useSelector } from 'react-redux';
import { Typewriter } from 'react-simple-typewriter';
import PropagateLoader from 'react-spinners/PropagateLoader';
import SyncLoader from 'react-spinners/SyncLoader';
import { convertToCSV, downloadCSV } from '../../../common/commonfunctions';
import { chatFeedback, resetChatFeedback } from '../../../redux/chatApp/chatFeedbackSlice';
import { chatPost, resetChatPost } from '../../../redux/chatApp/chatPostSlice';
import { fetchHistoryChats } from '../../../redux/chatApp/HistoryChat';
import { fetchNewChat, newChatReset } from '../../../redux/chatApp/NewChat';
import { resetSpecificChat } from '../../../redux/chatApp/SpecificChat';
import { getTokenSpeechApi } from '../../../redux/chatApp/tokenSpeech';
import './chatBot.css';
import DynamicGraph from './DynamicGraph';
import TableData from './TableData';

const { TextArea } = Input;

interface Message {
	chatGuId: number;
	text: string;
	sender: 'user' | 'bot';
	timestamp: string;
}

const ChatBot = () => {
	
	const { chatResponse, status: chatResponseLoader } = useSelector((state: any) => state?.chatPostReducer);
	const { specificChatResponse, status: specificChatLoader, lastRequest, newStatus } = useSelector((state: any) => state?.specificChatReducer);
	const { chatFeedbackResponse, status: feedbackLoader } = useSelector((state: any) => state?.chatFeedbackReducer);
	const { newChatData, status: newChatLoader } = useSelector((state: any) => state?.NewChatReducer);
	const [open, setOpen] = useState(false);
	const [openTable, setOpenTable] = useState(false);
	const [tabledata, setTableData] = useState<any>();
	const [graphData, setGraphData] = useState<any>();

	message.config({
		maxCount: 1
	});

	const handleTableChange = (Tablecontent: any) => {
		setOpenTable(true);
		setTableData(Tablecontent);
	};
	const handleGraph = (graphcontent: any) => {
		setOpen(true);
		setGraphData(graphcontent);
	};

	const [chatsession, setChatSession] = useState<any>('');
	const [messages, setMessages] = useState<any[]>([
		{
			chatGuId: 0,
			response: 'How can I help you with?',
			// request: 'hi',
			type: 'text'
			// sender: 'bot',
		}
	]);

	useEffect(() => {
		dispatch(fetchNewChat());
	}, []);

	useEffect(() => {
		if (newChatData?.statusCode === 200) {
			setMessages([
				{
					chatGuId: Date.now(),
					response: 'How can I help you with?',
					request: '',
					type: 'text'
				}
			]);

			setChatSession(newChatData?.data?.chatSessionGuId);
			dispatch(newChatReset());
		} else if (newChatData?.statusCode === 300 || newChatData?.statusCode === 500) {
			dispatch(newChatReset());
		}
	}, [newChatData]);

	useEffect(() => {
		if (chatFeedbackResponse?.statusCode === 200) {
			const chatGuid: any = chatFeedbackResponse?.data?.chatGuId;
			const isLiked: any = chatFeedbackResponse?.data?.isLiked;

			if (chatGuid) {
				setMessages((prevMessages) =>
					prevMessages?.map((message: any) => (message?.chatGuId === chatGuid ? { ...message, isLiked: isLiked } : message))
				);
			}

			message.success(
				<p style={{ fontFamily: 'NoirPro' }} className="gx-p-0 gx-m-0 gx-fs-sm">
					Thanks for your feedback
				</p>
			);
			dispatch(resetChatFeedback());
		} else if (chatFeedbackResponse?.statusCode === 300 || chatFeedbackResponse?.statusCode === 500) {
			message.warning(chatFeedbackResponse?.message);
			dispatch(resetChatFeedback());
		}
	}, [chatFeedbackResponse]);

	useEffect(() => {
		if (specificChatResponse?.statusCode === 200 && Array?.isArray(specificChatResponse?.data)) {
			setChatSession(lastRequest?.ChatSessionGuId);

			const cleanedData = specificChatResponse?.data?.map((item: any) => {
				let parsedResponse: any = item?.response;

				if (item?.type !== 'text') {
					try {
						parsedResponse = JSON?.parse(item?.response);
					} catch (error) {
						console.error('Error parsing response:', error);
					}
				}

				return {
					...item,
					response: parsedResponse
				};
			});
			setMessages([
				{
					chatGuId: 0,
					response: 'How can I help you with?',
					type: 'text'
				},
				...cleanedData
			]);

			dispatch(resetSpecificChat());
		} else if (specificChatResponse?.statusCode === 400 || specificChatResponse?.statusCode === 500) {
			console.error('Error in API response:', specificChatResponse);
			dispatch(resetSpecificChat());
		}
	}, [specificChatResponse]);

	useEffect(() => {
		if (chatResponse?.statusCode === 200) {
			const cleanedData = (() => {
				let parsedResponse: any = chatResponse?.data?.response;

				if (chatResponse?.data?.type !== 'text') {
					try {
						parsedResponse = JSON?.parse(chatResponse?.data?.response);
					} catch (error) {
						console.error('Error parsing response:', error);
					}
				}

				return {
					...chatResponse?.data,
					response: parsedResponse
				};
			})();
			dispatch(fetchHistoryChats());
			setMessages((prevMessages: any) => [...prevMessages, cleanedData]);

			dispatch(resetChatPost());
		} else if (chatResponse?.statusCode == 400 || chatResponse?.statusCode == 500) {
			message?.warning(chatResponse?.message);
			dispatch(resetChatPost());
		}
	}, [chatResponse]);

	const canvasRef = useRef<any>(null);
	const chatContainerRef = useRef<HTMLDivElement>(null);
	const timestampYear = new Date().toLocaleString('en-US', {
		weekday: 'long',
		year: 'numeric'
	});
	const dispatch = useDispatch();

	// useEffect(() => {
	// 	if (chatContainerRef.current) {
	// 		chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
	// 	}
	// }, [messages]);

	useLayoutEffect(() => {
		if (chatContainerRef.current) {
			chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
		}
	}, [messages]);

	const [isListening, setIsListening] = useState<boolean>(false);
	const [transcribedText, setTranscribedText] = useState<any>('');
	const recognizerRef = useRef<sdk.SpeechRecognizer | null>(null);

   // Dropdown list for time stamp
	const options = [
		{ value: '3000', label: '3s' },
		{ value: '5000', label: '5s' },
		{ value: '10000', label: '10s' },
		{ value: '15000', label: '15s' }
	 ];


	const [silenceTimeoutDuration, setSilenceTimeoutDuration] = useState<any>(options[0]);
	// const [silenceTimeoutDuration, setSilenceTimeoutDuration] = useState<any>({ value: '3000', label: '3s' });
	// const [silenceTimeoutDuration, setSilenceTimeoutDuration] = useState<any>(3000); //old
	const [isButtonDisabled, setIsButtonDisabled] = useState(false);
	const silenceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

	
	const cooldownDuration = 2000;

	const handleTimeoutChange = (value: any) => {
		// setSilenceTimeoutDuration(value);
		// setSilenceTimeoutDuration(Number(value));
		const selectedOption = options.find(option => option.value === value);
		if (selectedOption) {
		  setSilenceTimeoutDuration(selectedOption);
		}
	};

	const toggleListening = async () => {
		if (isButtonDisabled) return;

		if (isListening) {
			await stopListening();
		} else {
			await startListening();
		}
	};


	const stopListening = async () => {
		if (recognizerRef?.current) {
			// Clear the active silence timeout if it exists
			if (silenceTimeoutRef.current) {
				clearTimeout(silenceTimeoutRef.current);
				silenceTimeoutRef.current = null;
			}
			try {
				await new Promise<void>((resolve, reject) => {
					recognizerRef.current!.stopContinuousRecognitionAsync(
						// () => {
						// 	setIsListening(false);
						// 	resolve();
						// },
						// (err) => reject(err)

						async () => {
							setIsListening(false);
							// Auto trigger function while ending speaking after 3 sec.
							const inputText = form.getFieldValue('inputText');
							if (inputText && inputText.trim()) {
							  await new Promise(res => setTimeout(res, 500)); // optional delay for smoothness
							  form.submit(); // 👉 Auto-submit form if input exists
							}
							resolve();
						 },
						 (err) => reject(err)
					);
				});
			} catch (error) {
				console.error('Error stopping recognition:', error);
			} finally {
				recognizerRef.current = null;
			}
		}
	};

	const startListening = async () => {
		if (isListening) {
			await stopListening();
			return;
		}

		let speechToken: any = sessionStorage.getItem('speechToken');
		let region: any = sessionStorage.getItem('speechRegion');

		const currentTime = Math.floor(Date.now() / 1000);

		if (speechToken && region) {
			try {
				const decodedToken: any = decodeToken(speechToken);
				if (decodedToken?.exp && decodedToken.exp <= currentTime) {
					speechToken = null;
					region = null;
					sessionStorage.removeItem('speechToken');
					sessionStorage.removeItem('speechRegion');
				}
			} catch (error) {
				console.error('Error decoding token:', error);
				speechToken = null;
				region = null;
				sessionStorage.removeItem('speechToken');
				sessionStorage.removeItem('speechRegion');
			}
		}

		if (!speechToken || !region) {
			const response: any = await dispatch(getTokenSpeechApi());

			if (response?.payload?.data?.statusCode === 200) {
				speechToken = response?.payload?.data?.data?.token;
				region = response?.payload?.data?.data?.region;

				sessionStorage.setItem('speechToken', speechToken);
				sessionStorage.setItem('speechRegion', region);
			} else {
				console.error('Failed to retrieve token:', response.message);
				return;
			}
		}

		setIsListening(true);
		setIsButtonDisabled(true);

		setTimeout(() => {
			setIsButtonDisabled(false);
		}, cooldownDuration);

		const speechConfig = sdk.SpeechConfig.fromAuthorizationToken(speechToken, region);
		speechConfig.speechRecognitionLanguage = 'en-US';

		const audioConfig = sdk.AudioConfig.fromDefaultMicrophoneInput();
		const recognizer = new sdk.SpeechRecognizer(speechConfig, audioConfig);
		recognizerRef.current = recognizer;
		let interimResult = '';
		let silenceTimeout: NodeJS.Timeout;

		const resetSilenceTimeout = () => {
			if (silenceTimeoutRef.current) {
				clearTimeout(silenceTimeoutRef.current);
			}
			silenceTimeoutRef.current = setTimeout(() => {
				stopListening();
			}, Number(silenceTimeoutDuration?.value));
		};

	

		recognizer.recognizing = (s, e) => {
			interimResult = e.result.text;

			const prevText = form.getFieldValue('inputText') || '';
			const lines = prevText.split('\n');

			lines[lines.length - 1] = interimResult;

			form.setFieldsValue({
				inputText: lines.join('\n')
			});

			resetSilenceTimeout();
		};

		recognizer.recognized = (s, e) => {
			if (e.result.reason === sdk.ResultReason.RecognizedSpeech) {
				const prevText = form.getFieldValue('inputText') || '';
				const lines = prevText.split('\n');

				lines[lines.length - 1] = e.result.text; // Finalize last line
				lines.push('');

				form.setFieldsValue({
					inputText: lines.join('\n')
				});

				interimResult = '';
				resetSilenceTimeout();
			}
		};

		recognizer.startContinuousRecognitionAsync(
			() => {
				resetSilenceTimeout();
			},
			(err) => {
				console.error('Error starting recognition:', err);
				setIsListening(false);
			}
		);
	};

	// const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
	// 	if (e.key === 'Enter' && !e.shiftKey) {
	// 		e.preventDefault();
	// 		handleSendMessage();
	// 	} else if (e.shiftKey && e.key === 'Enter') {
	// 		e.preventDefault();
	// 		setTranscribedText((prevText: any) => prevText + '\n');
	// 	}
	// };

	const [form] = Form.useForm();
	const [hasError, setHasError] = useState(false);

	const onFinish = (values: any) => {
		if (!values.inputText?.trim()) {
			setHasError(true); // Show red border when input is empty
			message.warning('Please enter a message');
			return; // Stop submission
		}

		setHasError(false);
		form.resetFields();

		const newUserMessage: any = {
			chatGuId: Date.now(),
			response: '',
			request: values?.inputText,
			type: 'text'
		};

		dispatch(
			chatPost({
				Prompt: values?.inputText,
				ChatSessionGuid: chatsession
			})
		);

		setMessages((prevMessages: any) => [...prevMessages, newUserMessage]);
	};

	const containsHTML = (str: any) => /<\/?[a-z][\s\S]*>/i.test(str);

	const feedbackClick = ({ chatGuId, isLiked, reason }: any) => {
		const payload = reason ? { chatGuId, isLiked, reason } : { chatGuId, isLiked };

		dispatch(chatFeedback(payload));
	};

	const [feedbacktext, setFeedbacktext] = useState<any>('');

	const handleExport = (data: any) => {
		const csvData = convertToCSV(data);
		downloadCSV(csvData, `TABLE_DATA`);
	};

	return (
		<>
			<motion.div initial={{ y: 400, opacity: 0.5 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.9, ease: easeInOut }}>
				<div className="ant-card ant-card-bordered gx-card boxshadow gx-m-0 gx-p-0 chatarea" style={{ borderRadius: '0px' }}>
					<div
						className="ant-card-body gx-px-0 gx-py-1"
						style={{ display: 'flex', justifyContent: 'center', flexDirection: 'column', alignItems: 'center', height: '100%' }}
					>
						<div className="gx-chat-main" style={{ width: '100%', height: '100%' }}>
							<div
								className="ChatMainBody"
								style={{
									// backgroundImage: `url(/assets/images/whatsapp.jpg)`
									background: 'linear-gradient(315deg,rgb(238, 241, 255) 1% 0%,#ffffff )',
									overflow: 'hidden'
								}}
							>
								<div className="stars"></div>
								<div className="stars2"></div>
								<div className="stars3"></div>
								<div className="stars"></div>
								<div className="stars2"></div>
								<div className="stars3"></div>

								{/* <div className="moving-bubble1"></div> */}
								<div
									ref={chatContainerRef}
									style={{
										position: 'absolute',
										inset: 0,
										overflow: 'scroll',
										marginRight: '-15px',
										marginBottom: '-15px'
									}}
								>
									{/* <div className="centered-text gx-mt-2" style={{fontWeight:500}}>{timestampYear}</div> */}
									{newStatus ? (
										<div style={{ backgroundColor: 'red', display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: '10px' }}>
											<PropagateLoader speedMultiplier={0.8} color="#03A9F5" size={12} />
										</div>
									) : (
										<div className="gx-chat-main-content">
											{messages?.map((mess: any) => {
												return (
													<div>
														{mess?.request && (
															<div className="gx-chat-item gx-fs-sm gx-flex-row-reverse">
																<div className="gx-status-pos">
																	<span className="gx-status gx-online"></span>
																	<span
																		className="ant-avatar ant-avatar-circle ant-avatar-image gx-size-40 gx-align-self-end boxshadow"
																		style={{ border: '1px solid black' }}
																	>
																		<img src="/assets/images/userlogo.png" alt="User Avatar" />
																	</span>
																</div>
																<div className="gx-bubble-block">
																	<motion.div
																		initial={{ x: 400, opacity: 0.5 }}
																		animate={{ x: 0, opacity: 1 }}
																		transition={{ duration: 1, ease: 'easeOut' }}
																	>
																		<div
																			className="gx-bubble boxshadow narrow-bubble"
																			style={{
																				backgroundColor: '#000',
																				color: 'white',
																				position: 'relative'
																			}}
																		>
																			<div className="gx-message">
																				<div className="gx-d-flex gx-align-items-center gx-fs-sm">
																					<p className="gx-p-0 gx-m-0">{mess?.request}</p>
																					<Tooltip
																						title={
																							<p className="gx-p-0 gx-m-0" style={{ fontFamily: 'NoirPro' }}>
																								Copy to Clipboard
																							</p>
																						}
																					>
																						<MdContentCopy
																							className="gx-link gx-ml-2"
																							style={{ color: '#FFCE00' }}
																							// onClick={() => copyToClipboard(mess.request)}
																							onClick={() => {
																								message.success('Copied to clipboard');
																								navigator?.clipboard?.writeText(mess?.request);
																							}}
																						/>
																					</Tooltip>
																				</div>
																			</div>
																		</div>
																	</motion.div>
																</div>
															</div>
														)}

														{mess?.response && (
															<div className="gx-chat-item gx-fs-sm">
																<div className="gx-status-pos">
																	{/* <span className="gx-status gx-online"></span> */}
																	<span
																		className="ant-avatar ant-avatar-circle ant-avatar-image gx-size-40 gx-align-self-end "
																		// style={{ border: '1px solid black' }}
																	>
																		<img src="/assets/images/psspl-sm.svg" alt="AI Avatar" />
																	</span>
																</div>
																<div className="gx-bubble-block">
																	<motion.div
																		initial={{ x: -400, opacity: 0.5 }}
																		animate={{ x: 0, opacity: 1 }}
																		transition={{ duration: 1, ease: 'easeOut' }}
																	>
																		<div
																			className={`gx-bubble boxshadow ${mess?.type === 'Graph' || mess?.type === 'table' ? 'wide-bubble' : 'narrow-bubble'}`}
																			style={{
																				// backgroundColor: 'rgb(238 238 238)',
																				position: 'relative'
																			}}
																		>
																			<div className="gx-message">
																				{mess?.type === 'text' && (
																					<div className="gx-d-flex gx-align-items-center gx-fs-sm">
																						<Typewriter words={[mess?.response]} typeSpeed={10} cursor={false} />
																						{/* {mess?.response} */}
																						<Tooltip
																							title={
																								<p className="gx-p-0 gx-m-0" style={{ fontFamily: 'NoirPro' }}>
																									Copy to Clipboard
																								</p>
																							}
																						>
																							<MdContentCopy
																								className="gx-link gx-ml-2"
																								onClick={() => {
																									message.success('Copied to clipboard');
																									navigator?.clipboard?.writeText(mess?.response);
																								}}
																							/>
																						</Tooltip>
																					</div>
																				)}
																				{mess?.type === 'table' && (
																					<div style={{ display: 'flex', flexDirection: 'column' }}>
																						{/* {containsHTML(mess?.response) ? (
                                // eslint-disable-next-line
                                <div dangerouslySetInnerHTML={{ __html: mess?.response }} />
                            ) : ( */}

																						<div className="gx-mb-1" style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
																							{mess?.response?.length >= 100 ? (
																								<Alert
																									style={{ fontFamily: 'NoirPro', width: '97%' }}
																									className="gx-my-0 gx-fs-sm gx-p-1 gx-mt-1"
																									message="Note: The table displays only the top 100 records. Click the download option to view the full data."
																									type="warning"
																									showIcon
																								/>
																							) : (
																								<div></div>
																							)}

																							{mess?.response?.length > 10 &&
																							<Tooltip
																							title={
																								<p style={{ fontFamily: 'NoirPro' }} className="gx-p-0 gx-m-0 ">
																									Download csv export file
																								</p>
																							}
																							placement="topLeft"
																						>

																							<Button
																								type="primary"
																								shape="circle"
																								className="gx-m-0 gx-p-0 gx-mb-2 gx-mt-2 gx-ml-1 boxshadow"
																								style={{ alignSelf: 'end' }}
																								icon={<DownloadOutlined />}
																								size={'small'}
																								onClick={() => handleExport(mess?.response)}
																							/>
																						</Tooltip>
																							}

																							
																						</div>
																						<TableData data={mess?.response} openTable={openTable} />
																						{/* )} */}
																					</div>
																				)}
																				{mess?.type === 'graph' && (
																					<>
																						<DynamicGraph data={mess?.response} />
																					</>
																				)}
																			</div>

																			<div className="gx-d-flex gx-align-items-center gx-justify-content-between" style={{ width: '100%' }}>
																				{mess?.type === 'table' && (
																					<Button
																						icon={<FaEye color="white" className="gx-mr-1" />}
																						size="small"
																						type="primary"
																						className="gx-m-0 gx-mt-3"
																						style={{ alignSelf: 'end' }}
																						onClick={() => handleTableChange(mess?.response)}
																					>
																						View
																					</Button>
																				)}

																				{mess?.type === 'graph' && (
																					<Button
																						icon={<FaEye color="white" className="gx-mr-1" />}
																						size="small"
																						type="primary"
																						className="gx-m-0 gx-mt-3"
																						onClick={() => handleGraph(mess?.response)}
																					>
																						View
																					</Button>
																				)}

																				<div></div>

																				<div className="gx-d-flex gx-align-items-center">
																					{mess?.type === 'text' && mess?.response === 'How can I help you with?' ? null : (
																						<div className="gx-d-flex gx-align-items-center">
																							{mess?.isLiked === true ? (
																								<AiFillLike
																									size={15}
																									className="gx-mt-1 gx-mr-2 gx-link likeIcon"
																									style={{ display: 'flex', alignSelf: 'end', cursor: 'default' }}
																								/>
																							) : (
																								<Tooltip
																									title={
																										<p
																											className="gx-m-0 gx-p-0 gx-fs-sm gx-d-flex gx-align-items-center"
																											style={{ fontFamily: 'NoirPro', color: 'black' }}
																										>
																											<AiFillLike className="gx-link gx-mr-1" style={{ cursor: 'default' }} size={15} />
																											<span className="gx-m-0 gx-p-0 gx-font-weight-light">Good response</span>
																										</p>
																									}
																									placement="bottom"
																									color="white"
																								>
																									<AiOutlineLike
																										size={15}
																										className="gx-mt-1 gx-mr-2 gx-link likeIcon"
																										style={{ display: 'flex', alignSelf: 'end' }}
																										onClick={() => feedbackClick({ chatGuId: mess?.chatGuId, isLiked: true })}
																									/>
																								</Tooltip>
																							)}

																							{mess?.isLiked === false ? (
																								<AiFillDislike
																									size={15}
																									className="gx-mt-1 gx-mr-2 gx-link likeIcon"
																									style={{ display: 'flex', alignSelf: 'end', cursor: 'default' }}
																								/>
																							) : (
																								<Popover
																									zIndex={8000}
																									placement="right"
																									content={
																										<div
																											style={{
																												width: '100%',
																												fontFamily: 'NoirPro',
																												display: 'flex',
																												flexDirection: 'column'
																												// justifyContent: 'end',
																												// alignItems: 'end'
																											}}
																										>
																											<p className="gx-p-0 gx-m-0 gx-fs-xs gx-mb-1">
																												<img src="/assets/images/dislike.gif" alt="AI Avatar" width={23} />
																												Please provide a reason for your dislike.
																											</p>
																											<TextArea
																												maxLength={100}
																												onChange={(e) => setFeedbacktext(e.target.value)}
																												style={{ fontFamily: 'NoirPro', width: '250px' }}
																												className="gx-fs-xs"
																												// size="small"
																												placeholder="Please Provide a Reason"
																											/>
																											<Button
																												type="primary"
																												className="gx-fs-xs gx-mt-2 boxshadow"
																												size="small"
																												style={{ width: '50%', alignSelf: 'end' }}
																												disabled={!feedbacktext}
																												onClick={() =>
																													feedbackClick({ chatGuId: mess?.chatGuId, isLiked: false, reason: feedbacktext })
																												}
																												loading={feedbackLoader}
																											>
																												Submit
																											</Button>
																										</div>
																									}
																									trigger="click"
																								>
																									<Tooltip
																										title={
																											<p
																												className="gx-m-0 gx-p-0 gx-fs-sm gx-d-flex gx-align-items-center"
																												style={{ fontFamily: 'NoirPro', color: 'black' }}
																											>
																												<AiFillDislike className="gx-link gx-mr-1" style={{ cursor: 'default' }} size={15} />
																												<span className="gx-m-0 gx-p-0 gx-font-weight-light">Bad response</span>
																											</p>
																										}
																										placement="bottom"
																										color="white"
																									>
																										<AiOutlineDislike
																											size={15}
																											className="gx-mt-1 gx-mr-2 gx-link likeIcon"
																											style={{ display: 'flex', alignSelf: 'end' }}
																										/>
																									</Tooltip>
																								</Popover>
																							)}
																						</div>
																					)}
																				</div>
																			</div>
																		</div>
																	</motion.div>
																</div>
															</div>
														)}
													</div>
												);
											})}
										</div>
									)}
								</div>

								{chatResponseLoader ? (
									<div
										className="gx-p-0 gx-m-0 gx-ml-1 "
										style={{
											position: 'absolute',
											bottom: '0px',
											display: 'flex',
											alignItems: 'center',
											color: 'green',
											backgroundColor: '#ffffff42',
											// padding: '5px',
											borderRadius: '10px'
										}}
									>
										<img src="/assets/images/psspl-sm.svg" alt="active" style={{ width: 40 }} className="gx-p-0 gx-m-0 gx-mr-1" />

										<p className="gx-p-0 gx-m-0 gx-mt-2">Analyzing your question </p>
										<SyncLoader speedMultiplier={0.8} color="green" size={7} className="gx-ml-1 gx-p-0 g-m-0" />
									</div>
								) : null}
							</div>

							<div className="gx-chat-main-footer  gx-p-0 gx-px-3 gx-pb-1 gx-m-0 ">
								<div className="gx-flex-row gx-align-items-center ">
									<Popover
										zIndex={8000}
										content={
											<>
												<div>
													<div>
														<p className="gx-m-0 gx-p-0 gx-fs-sm gx-d-flex gx-align-items-center" style={{ fontFamily: 'NoirPro' }}>
															<img src="/assets/images/microphone.gif" alt="active" style={{ width: 30 }} className="gx-p-0 gx-m-0 gx-mr-1" />
															<span style={{ fontWeight: 500 }}>Use Voice Mod</span>
														</p>
													</div>
													<hr className="gx-p-0 gx-m-0 gx-my-1" />
													<p className="gx-m-0 gx-p-0 gx-fs-sm gx-d-flex gx-align-items-center">
														<img src="/assets/images/configurationEmail.gif" alt="active" style={{ width: 35 }} className="gx-p-0 gx-m-0 gx-mr-1" />
														<span style={{ fontFamily: 'NoirPro' }} className="gx-p-0 gx-m-0">
															Click to start voice input and convert speech to text seamlessly <br />
															<Alert
																style={{ fontFamily: 'NoirPro' }}
																className="gx-my-0 gx-fs-sm gx-p-1 gx-mt-1"
																message="Note: This feature is currently available only for English."
																type="warning"
																showIcon
															/>
														</span>
													</p>
												</div>
											</>
										}
										trigger="hover"
									>
										<button
											type="button"
											disabled={isButtonDisabled}
											className={`mic-button ${isListening ? 'listening' : ''}`}
											onClick={toggleListening}
										>
											<div className="mic-icon"></div>
											<div className="ripple"></div>
										</button>
									</Popover>

									<div className="gx-mt-4" style={{ cursor: 'pointer' }}>
										<Popover
											zIndex={8000}
											placement="top"
											content={
												<div
													style={{
														width: '100%',
														fontFamily: 'NoirPro',
														display: 'flex',
														flexDirection: 'column'
													}}
												>
													<p className=" gx-p-0 gx-m-0 gx-fs-sm gx-mb-1 ">
														<img src="/assets/images/settings.gif" className="gx-mr-1" alt="AI Avatar" width={23} />
														Configure Speech Recognition
													</p>
													<p className=" gx-p-0 gx-m-0 gx-fs-xs gx-mb-1 gx-font-weight-light">
														Adjust the silence timeout setting below to control <br /> how long the speech recognizer should wait during <br />{' '}
														periods of silence before automatically stopping.
														<br /> This helps manage recognition sessions efficiently.
													</p>
													<Select
														style={{ width: '100%', fontFamily: 'NoirPro' }}
														className="gx-fs-xs"
														size="small"
														value={silenceTimeoutDuration}
														onChange={handleTimeoutChange}
														options={options}
														// options={[
														// 	{ value: '3000', label: '3s' },
														// 	{ value: '5000', label: '5s' },
														// 	{ value: '10000', label: '10s' },
														// 	{ value: '15000', label: '15s' }
														// ]}
													/>
												</div>
											}
											trigger="click"
										>
											<Tooltip
												placement="left"
												title={
													<p className="gx-m-0 gx-p-0" style={{ fontFamily: 'NoirPro' }}>
														Configure your Mic Delay
													</p>
												}
											>
												<img src="/assets/images/settings.gif" alt="User Avatar" width={24} />
											</Tooltip>
										</Popover>
									</div>

									<div className="gx-col gx-pl-1 gx-p-0 gx-m-0">
										<div className="gx-form-group gx-fs-xs gx-p-0 gx-m-0">
											{/* <textarea
												className="ant-input gx-mt-1 gx-fs-xs"
												style={{ height: 'auto', fontSize: '14px', resize: 'none' }}
												value={input}
												placeholder="Speak/Type to Generate the Text..."
												onChange={(e: any) => setInput(e?.target?.value)}
												onKeyDown={(e: React.KeyboardEvent<HTMLTextAreaElement>) => handleKeyPress(e)}
												rows={2}
											/> */}
											<Form
												className="gx-form-group gx-d-flex gx-m-0 gx-p-0"
												form={form}
												layout="vertical"
												onFinish={onFinish}
												initialValues={{ inputText: '' }} // Default initial value
												style={{ height: '80px ', borderColor: hasError ? 'red' : '' }}
											>
												<Form.Item
													name="inputText"
													style={{ width: '100%', height: '50%' }}
													className="gx-m-0 gx-p-0"
													// rules={[{ required: true}]}
												>
													<Input.TextArea
														className="gx-mt-1 gx-fs-xs gx-m-0"
														style={{ height: 'auto', fontSize: '14px', resize: 'none', width: '100%', fontFamily: 'NoirPro' }}
														placeholder="Speak/Type to Generate the Text..."
														onKeyDown={(e) => {
															if (e.key === 'Enter' && !e.shiftKey) {
																e.preventDefault();
																form.submit();
															}
														}}
														rows={3}
													/>
												</Form.Item>

												<Tooltip title="Send Message">
													<div className="sendicon " style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
														{/* <IoIosSend size={30} className="gx-mt-3 " />
										<p className="gx-fs-sm gx-m-0 gx-p-0">Send</p> */}
														<Form.Item>
															{/* <Button type="primary" htmlType="submit">
          Submit
        </Button> */}
															<Button
																htmlType="submit"
																className="gx-mt-3 gx-ml-2 sendbtnshadow"
																shape="default"
																icon={<IoMdSend size={20} className="gx-mt-1 gx-ml-1 " style={{ color: 'white' }} />}
																style={{ backgroundColor: '#000' }}
															/>
														</Form.Item>
													</div>
												</Tooltip>
											</Form>
										</div>
									</div>
									{/* <i className="gx-icon-btn icon icon-sent"></i> */}
								</div>
							</div>
						</div>
					</div>
				</div>
				<div
					className="gx-chat-main-footer gx-p-0 gx-px-3 gx-pb-1 gx-m-0"
					style={{
						position: 'absolute',
						bottom: 0,
						left: 0,
						right: 0, 
						backgroundColor: '#2B2A33',
						textAlign: 'center',
						padding: '8px 16px',
						color: '#fff',
					}}
				>
					{new Date().getFullYear()} © Prowess Selling Skills Pvt. Ltd. All rights reserved.
				</div>

			</motion.div>

			<Modal
				open={open}
				onCancel={() => setOpen(false)}
				style={{ top: 4 }}
				width={1300}
				okText={false}
				cancelText={false}
				footer={false}
				closeIcon={<GiSplitCross title="close" size={17} className="gx-m-0 gx-mr-2 gx-p-0 gx-fs-sm gx-text-danger" />}
			>
				<div className="gx-p-3 gx-d-flex gx-align-items-center gx-justify-content-center">
					<DynamicGraph data={graphData} open={open} />
				</div>
			</Modal>
			<Modal
				open={openTable}
				onCancel={() => setOpenTable(false)}
				style={{ top: '10%' }}
				// style={{ top: 4 }}
				width={1300}
				okText={false}
				cancelText={false}
				footer={false}
				closeIcon={<GiSplitCross title="close" size={17} className="gx-m-0 gx-mr-3 gx-p-0 gx-fs-sm gx-text-danger" />}
			>
				{/* eslint-disable-next-line  */}
				{/* <div className="table-container gx-p-3 " dangerouslySetInnerHTML={{ __html: tabledata }}></div> */}
				<div style={{ overflow: 'auto' }}>
					<TableData data={tabledata} />
				</div>
			</Modal>
		</>
	);
};

export default ChatBot;
